import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import { PrimaryBackground } from '../../../app/theme/tokens/colors';
import {
    ShouldVerifyEmail,
    ShouldVerifyEmailFMA,
    ShouldVerifySMS,
} from '../../../shared/messages/messages';
import { UserShouldRedefineHisPword } from '../../../shared/messages/errors';
import { LoginForm, LoginImage } from '../components/LoginComponents';
import { LoginService } from '../../../services/auth';

import { useResponsive } from '../../../hooks/useResponsive';

// COMPONENTES DE CARD
import { LoginCard } from '../components/LoginCard';
import { ForgotPasswordRequestCard } from '../components/ForgotPasswordRequestCard';
import { ForgotPasswordSubmitCard } from '../components/ForgotPasswordSubmitCard';
import { RedefinePasswordCard } from '../components/RedefinePasswordCard';
import { VerifyEmailCard } from '../components/VerifyEmailCard';
import { EmailConfirmSignupCard } from '../components/EmailConfirmSignupCard';
import { EmailResetPasswordCard } from '../components/EmailResetPasswordCard';
import { EmailVerifyAttributeCard } from '../components/EmailVerifyAttributeCard';
import { VerifyCodeCard } from '../components/VerifyCode';
import { RecoveryEmailSentCard } from '../components/RecoveryEmailSentCard';

const year = new Date().getFullYear();
const loginService = LoginService;

// ─── Mapeamento de erros MFA ────────────────────────────────────────────────
/**
 * Converte o `reason` cru vindo da API em uma mensagem amigável para o usuário.
 */
const mapMFAError = (reason) => {
    if (
        reason.includes('Incorrect username or password') ||
        reason.includes('Incorrect')
    ) {
        return {
            message: 'Código incorreto. Verifique o código e tente novamente.',
            isExpired: false,
        };
    }
    if (reason.includes('Invalid session') || reason.includes('expired')) {
        return {
            message:
                'Código expirado. Clique em "Reenviar código" e complete o captcha abaixo para solicitar um novo código.',
            isExpired: true,
        };
    }
    if (reason.includes('MISSING_CAPTCHA')) {
        return {
            message: 'Complete o captcha para reenviar o código.',
            isExpired: false,
        };
    }
    // Mensagem padrão para erros não mapeados
    return {
        message: 'Não foi possível validar o código. Tente novamente.',
        isExpired: false,
    };
};

/* ===== Layout externo ===== */

const DivDisplay = styled.div`
  display: ${(props) => props.display || 'flex'};
`;

const DivPanel5 = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
`;

const DivPanel7 = styled.div`
  background-color: transparent;
  box-sizing: border-box;
  margin: 0 auto;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const SpanRights = styled.span`
  position: fixed;
  bottom: 0px;
  right: 0;
  padding: 7px;
  font-weight: bold;
  font-size: 10px;
`;

const FullBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  background-image: url(/loginImage.jpg);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  box-sizing: border-box;
  background-color: ${PrimaryBackground};
`;

export const LoginPage = () => {
    const { isDesktop } = useResponsive();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [keepConnected, setKeepConnected] = useState(false);
    const [isLogin, setIsLogin] = useState(false);
    const [message, setMessage] = useState('');
    const [verifyMessage, setVerifyMessage] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [shouldRedefinePassword, setShouldRedefinePassword] = useState(false);
    const [shouldVerifyEmailState, setShouldVerifyEmailState] = useState(false);
    const [shouldRecoveryPassword, setRecoveryPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [altchaPayload, setAltchaPayload] = useState(null);
    // Aviso exibido quando o usuário chega via SSO sem vínculo (precisa confirmar senha uma vez)
    const [ssoLinkNotice, setSsoLinkNotice] = useState('');

    // ── Estados exclusivos do fluxo /verify ────────────────────────────────────
    /** Payload do captcha exibido dentro do VerifyCodeCard */
    const [verifyAltchaPayload, setVerifyAltchaPayload] = useState(null);
    /**
     * Quando `true`, o VerifyCodeCard abre automaticamente a seção de captcha
     * (usado para sinalizar código expirado sem que o usuário precise clicar em "Reenviar").
     */
    const [forceShowRecaptcha, setForceShowRecaptcha] = useState(false);

    /* === Detectar primeiro acesso via query params === */
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        const username = params.get('username');

        // Retorno do fluxo SSO sem match: guarda o id opaco e mostra o aviso de confirmação.
        const ssoPending = params.get('ssoPending');
        const linkSSO = params.get('linkSSO');
        if (ssoPending) {
            sessionStorage.setItem('ssoPending', ssoPending);
        }
        if (linkSSO === '1') {
            setSsoLinkNotice(
                'Confirme sua senha uma vez para ativar seu acesso único (SSO). ' +
                'Nas próximas vezes, o login pela conta White Martins será automático.'
            );
        }

        if (action === 'firstAccess' && username) {
            setEmail(String(username).trim());
            setShouldRedefinePassword(true);
            setMessage('Defina sua senha para acessar o Portal Homecare');
            return;
        }

        const storedMfaEmail = sessionStorage.getItem('mfaEmail');
        if (storedMfaEmail) {
            setEmail(String(storedMfaEmail).trim());
        }
    }, []);

    /* === Utilitários === */

    const trimOrEmpty = (value) =>
        typeof value === 'string' ? value.trim() : '';

    const syncTrimmedState = (
        emailValue,
        passwordValue,
        newPasswordValue,
        verificationCodeValue
    ) => {
        if (typeof emailValue === 'string' && emailValue !== email)
            setEmail(emailValue);
        if (typeof passwordValue === 'string' && passwordValue !== password)
            setPassword(passwordValue);
        if (
            typeof newPasswordValue === 'string' &&
            newPasswordValue !== newPassword
        )
            setNewPassword(newPasswordValue);
        if (
            typeof verificationCodeValue === 'string' &&
            verificationCodeValue !== verificationCode
        )
            setVerificationCode(verificationCodeValue);
    };

    /* === Ações de autenticação === */

    const authenticate = async (captchaPayload) => {
        if (!captchaPayload) {
            setMessage('Por favor, valide o captcha');
            return;
        }

        syncTrimmedState(trimOrEmpty(email), trimOrEmpty(password));
        setIsLogin(true);

        const result = await loginService.doAuth(
            trimOrEmpty(email),
            trimOrEmpty(password),
            keepConnected,
            captchaPayload
        );
        setIsLogin(false);

        console.log('[authenticate] result:', result);

        if (!result.logged) {
            if (result.message === ShouldVerifyEmailFMA) {
                sessionStorage.setItem('mfaEmail', trimOrEmpty(email));
                setVerifyMessage('');
                setMessage('');
                navigate('/verify');
                return;
            } else {
                setMessage(result.message);
                setVerifyMessage('');
            }
            setShouldRedefinePassword(result.message === UserShouldRedefineHisPword);
            setShouldVerifyEmailState(result.message === ShouldVerifyEmail);
            return;
        }

        setMessage('');
        const user = loginService.currentUser?.();
        if (user && user.shouldSignPrivacyTerm) {
            window.location.href = '/privacyTerm';
        } else {
            window.location.href = '/orders';
        }
    };

    const redefinePassword = async () => {
        syncTrimmedState(trimOrEmpty(email), undefined, trimOrEmpty(newPassword));
        setPassword('');
        setIsLogin(true);
        const result = await loginService.redefinePassword(
            trimOrEmpty(email),
            trimOrEmpty(newPassword),
            keepConnected
        );
        setIsLogin(false);
        if (!result.logged) {
            setMessage(result.message);
            setShouldRedefinePassword(result.message === UserShouldRedefineHisPword);
            setShouldVerifyEmailState(result.message === ShouldVerifyEmail);
        } else {
            setMessage('');
            if (result.user && result.user.shouldSignPrivacyTerm) {
                window.location.href = '/privacyTerm';
            } else {
                window.location.href = '/orders';
            }
        }
    };

    const verifyEmail = async () => {
        syncTrimmedState(
            trimOrEmpty(email),
            undefined,
            undefined,
            trimOrEmpty(verificationCode)
        );
        setIsLogin(true);
        const result = await loginService.verifyEmail(
            trimOrEmpty(verificationCode),
            keepConnected
        );
        setIsLogin(false);
        if (!result.logged) {
            setMessage(result.message);
            setShouldRedefinePassword(result.message === UserShouldRedefineHisPword);
            setShouldVerifyEmailState(result.message === ShouldVerifyEmail);
        } else {
            setMessage('');
        }
    };

    // ── Verificação do código MFA ──────────────────────────────────────────────
    const verifyMFACode = async (code) => {
        try {
            setIsLogin(true);
            const result = await loginService.confirmEmailOtp(code, email);

            if (result.status.code !== 200) {
                // `reason` é o campo adicionado em auth.ts para mapeamento fino
                const reason = result.status.reason || result.status.message || '';
                const { message: friendlyMessage, isExpired } = mapMFAError(reason);

                // Código expirado → abre captcha automaticamente no card
                if (isExpired) {
                    setForceShowRecaptcha(true);
                    // Reseta após um tick para permitir re-trigger caso já estivesse true
                    setTimeout(() => setForceShowRecaptcha(false), 100);
                }

                throw new Error(friendlyMessage);
            }

            const storage = keepConnected ? localStorage : sessionStorage;
            localStorage.setItem('keepConnected', keepConnected.toString());
            storage.setItem('portalHomeCareAuth', JSON.stringify(result.user));

            sessionStorage.removeItem('mfaEmail');
            setVerifyMessage('');
            navigate('/orders');
        } catch (err) {
            setVerifyMessage(
                err instanceof Error ? err.message : 'Erro ao validar código'
            );
        } finally {
            setIsLogin(false);
        }
    };

    const redirectToLoginInvalidSession = (customMessage) => {
        const msg =
            customMessage ||
            'Sessão de verificação inválida. Redirecionando para a tela de login...';
        setVerifyMessage(msg);

        setTimeout(() => {
            setVerifyMessage('');
            setMessage('');
            navigate('/login');
        }, 2000);
    };

    // ── Reenvio do código MFA ─────────────────────────────────────────────────
    /**
     * Recebe o captchaPayload preenchido pelo usuário no VerifyCodeCard.
     * O captcha é obrigatório; sem ele o backend rejeita com MISSING_CAPTCHA.
     */
    const resendCode = async (captchaPayload) => {
        const emailToResend = email || sessionStorage.getItem('mfaEmail') || '';

        if (!trimOrEmpty(emailToResend) || !trimOrEmpty(password)) {
            redirectToLoginInvalidSession();
            return;
        }

        const resp = await loginService.doLogin(
            trimOrEmpty(emailToResend),
            trimOrEmpty(password),
            captchaPayload // ← payload do captcha embutido no card
        );

        const statusCode = resp?.status?.code;
        const statusReason = resp?.status?.reason || resp?.status?.message || '';

        // Reenvio bem-sucedido → novo código enviado
        if (statusCode === 202) {
            // Reseta o payload do captcha no pai após uso
            setVerifyAltchaPayload(null);
            return;
        }

        // Login completo sem necessidade de MFA (caso raro)
        if (statusCode === 200) {
            setMessage('');
            const storage = keepConnected ? localStorage : sessionStorage;
            localStorage.setItem('keepConnected', keepConnected.toString());
            storage.setItem('portalHomeCareAuth', JSON.stringify(resp.user));
            navigate('/orders');
            return;
        }

        // Sessão inválida (usuário tentou reenviar sem ter feito login antes)
        if (statusReason.includes('password is required to signIn')) {
            redirectToLoginInvalidSession();
            return;
        }

        // Mapeia erros conhecidos (ex: MISSING_CAPTCHA) para mensagens amigáveis
        const { message: friendlyMessage } = mapMFAError(statusReason);
        throw new Error(
            friendlyMessage ||
            resp?.status?.message ||
            'Não foi possível reenviar o código.'
        );
    };

    const startRecovery = async (captchaPayload) => {
        syncTrimmedState(trimOrEmpty(email));
        setIsLogin(true);
        await loginService.forgotPassword(trimOrEmpty(email), captchaPayload);
        setIsLogin(false);
        setRecoveryPassword(true);
    };

    const forgotPasswordSubmit = async () => {
        syncTrimmedState(
            trimOrEmpty(email),
            undefined,
            trimOrEmpty(newPassword),
            trimOrEmpty(verificationCode)
        );
        setIsLogin(true);
        const result = await loginService.recoveryPassword(
            trimOrEmpty(email),
            trimOrEmpty(newPassword),
            trimOrEmpty(verificationCode)
        );
        setIsLogin(false);
        if (result && !result.sucess) {
            loginService.cleanAndRefresh();
        } else {
            setMessage(
                (result && result.message) ||
                'Erro ao redefinir a senha. Tente Novamente'
            );
        }
    };

    const onEnterTyped = (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            authenticate(altchaPayload);
        }
    };

    const onClickForgotPassword = () => {
        setMessage('');
        setAltchaPayload(null);
        window.location.pathname = '/recovery';
    };

    /* === Qual card renderizar === */

    const selectForm = () => {
        const path = window.location.pathname;

        if (path.indexOf('/recovery') >= 0) {
            if (shouldRecoveryPassword) {
                return (
                    <RecoveryEmailSentCard
                        email={email}
                        onBack={() => {
                            setRecoveryPassword(false);
                            setMessage('');
                            window.location.pathname = '/login';
                        }}
                    />
                );
            }
            return (
                <ForgotPasswordRequestCard
                    email={email}
                    message={message}
                    isLogin={isLogin}
                    onBack={() => {
                        setAltchaPayload(null);
                        window.location.pathname = '/login';
                    }}
                    onChangeEmail={setEmail}
                    onRecaptchaChange={setAltchaPayload}
                    onSubmit={() => startRecovery(altchaPayload)}
                />
            );
        }

        if (path.indexOf('/verify') >= 0) {
            const mfaEmail = email || sessionStorage.getItem('mfaEmail') || '';
            return (
                <VerifyCodeCard
                    email={mfaEmail}
                    message={verifyMessage}
                    onClearMessage={() => setVerifyMessage('')}
                    onVerifyClick={verifyMFACode}
                    onResendClick={resendCode}
                    recaptchaComponent={
                        <altcha-widget
                            challengeurl={process.env.REACT_APP_ALTCHA_CHALLENGE_URL}
                            onStateChange={(ev) => {
                                const payload =
                                    ev?.detail?.payload ?? ev?.target?.value ?? null;
                                setVerifyAltchaPayload(payload || null);
                            }}
                        />
                    }
                    captchaPayload={verifyAltchaPayload}
                    forceShowRecaptcha={forceShowRecaptcha}
                />
            );
        }

        if (path.indexOf('/email-confirm-signup') >= 0) {
            return <EmailConfirmSignupCard />;
        }

        if (path.indexOf('/email-reset-password') >= 0) {
            return <EmailResetPasswordCard />;
        }

        if (path.indexOf('/email-verify-attribute') >= 0) {
            return <EmailVerifyAttributeCard />;
        }

        if (shouldRedefinePassword) {
            return (
                <RedefinePasswordCard
                    email={email}
                    newPassword={newPassword}
                    message={message}
                    isLogin={isLogin}
                    showNewPassword={showNewPassword}
                    onChangeEmail={setEmail}
                    onChangeNewPassword={setNewPassword}
                    onToggleShowPassword={() => setShowNewPassword((prev) => !prev)}
                    onSubmit={redefinePassword}
                />
            );
        }

        if (shouldVerifyEmailState) {
            return (
                <VerifyEmailCard
                    email={email}
                    verificationCode={verificationCode}
                    message={message}
                    isLogin={isLogin}
                    onChangeEmail={setEmail}
                    onChangeCode={setVerificationCode}
                    onSubmit={verifyEmail}
                />
            );
        }

        return (
            <LoginCard
                email={email}
                password={password}
                keepConnected={keepConnected}
                showPassword={showPassword}
                isLogin={isLogin}
                message={message}
                ssoLinkNotice={ssoLinkNotice}
                onChangeEmail={setEmail}
                onChangePassword={setPassword}
                onToggleKeepConnected={() => setKeepConnected((prev) => !prev)}
                onToggleShowPassword={() => setShowPassword((prev) => !prev)}
                onForgotPassword={onClickForgotPassword}
                onEnterTyped={onEnterTyped}
                onSubmit={() => authenticate(altchaPayload)}
                onRecaptchaChange={setAltchaPayload}
            />
        );
    };

    useEffect(() => {
        if (loginService.isLogged()) {
            const user = loginService.currentUser();
            if (user.shouldSignPrivacyTerm) {
                window.location.href = '/privacyTerm';
            } else {
                window.location.href = '/orders';
            }
        }
    }, []);

    return (
        <>
            {isDesktop ? (
                <DivDisplay display="flex">
                    <LoginImage minWidth="650px" />
                    <LoginForm>
                        <DivPanel5>
                            <DivPanel7>{selectForm()}</DivPanel7>
                        </DivPanel5>
                    </LoginForm>
                    <SpanRights>2013 - {year} Todos os direitos reservados</SpanRights>
                </DivDisplay>
            ) : (
                <FullBackground>
                    <DivPanel7>{selectForm()}</DivPanel7>
                    <SpanRights>2013 - {year} Todos os direitos reservados</SpanRights>
                </FullBackground>
            )}
        </>
    );
};
